import * as i0 from '@angular/core';
import { Directive, Input, NgModule, InjectionToken, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, HostBinding, Injector, HostListener, EventEmitter, Self, Optional, Output } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/cdk/overlay';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { Subject, BehaviorSubject, Subscription, timer, fromEvent, take } from 'rxjs';
import { ComponentPortal } from '@angular/cdk/portal';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import * as i1$1 from '@angular/forms';
import * as i1$2 from '@eui/core';

class EuiTemplateDirective {
    constructor(template) {
        this.template = template;
    }
    getType() {
        return this.name;
    }
}
/** @nocollapse */ EuiTemplateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiTemplateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiTemplateDirective, selector: "[euiTemplate]", inputs: { type: "type", name: ["euiTemplate", "name"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiTemplate]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input,
                args: ['euiTemplate']
            }] } });
class EuiTemplateDirectiveModule {
}
/** @nocollapse */ EuiTemplateDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiTemplateDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirectiveModule, declarations: [EuiTemplateDirective], imports: [CommonModule], exports: [EuiTemplateDirective] });
/** @nocollapse */ EuiTemplateDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTemplateDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiTemplateDirective],
                    declarations: [EuiTemplateDirective],
                }]
        }] });

const showHide = trigger('showHide', [
    state('initial, void, hidden', style({ opacity: 0, transform: 'scale(0)' })),
    state('visible', style({ transform: 'scale(1)' })),
    transition('* => visible', animate('200ms cubic-bezier(0, 0, 0.2, 1)', keyframes([
        style({ opacity: 0, transform: 'scale(0)', offset: 0 }),
        style({ opacity: 0.5, transform: 'scale(0.99)', offset: 0.5 }),
        style({ opacity: 1, transform: 'scale(1)', offset: 1 }),
    ]))),
    transition('* => hidden', animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0 }))),
]);

const TOOLTIP_CONTAINER_CONFIG = new InjectionToken('TOOLTIP_CONTAINER_CONFIG');

class EuiTooltipConfig {
    constructor(values) {
        this.e2eAttr = 'eui-tooltip';
        this.tooltipContent = null;
        this.position = 'above';
        this.contentAlign = 'center';
        Object.assign(this, values);
    }
}

class EuiTooltipContainerComponent {
    constructor(config, cd) {
        this.config = config;
        this.cd = cd;
        this.e2e = this.config.e2eAttr;
    }
    get cssClasses() {
        return [
            'eui-tooltip',
            'eui-tooltip--' + this.config.position,
            'eui-tooltip--content-' + this.config.contentAlign,
        ].join(' ').trim();
    }
    get showHide() {
        return 'visible';
    }
}
/** @nocollapse */ EuiTooltipContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipContainerComponent, deps: [{ token: TOOLTIP_CONTAINER_CONFIG }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiTooltipContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiTooltipContainerComponent, selector: "eui-tooltip-container.component", host: { properties: { "attr.data-e2e": "this.e2e", "class": "this.cssClasses", "@showHide": "this.showHide" } }, ngImport: i0, template: "<div [innerHTML]=\"config.tooltipContent\"></div>\n", styles: [".eui-tooltip__container{background-color:transparent}.eui-tooltip__container .eui-tooltip{background-color:var(--eui-base-color-grey-100);border-radius:var(--eui-base-border-radius);box-shadow:var(--eui-base-shadow-4);color:var(--eui-base-color-white);font-size:var(--eui-base-font-size-xs);max-width:50vw;opacity:.95;padding:var(--eui-base-spacing-xs);white-space:pre-line}.eui-tooltip__container .eui-tooltip:before{border-color:transparent;border-style:solid;border-width:8px;bottom:0;content:\"\";display:block;height:0;position:absolute;width:0}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-left{text-align:left}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-right{text-align:right}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-center{text-align:center}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-justify{text-align:justify}.eui-tooltip__container .eui-tooltip.eui-tooltip--above{margin-bottom:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--above:before{border-color:var(--eui-base-color-grey-100) transparent transparent transparent;left:calc(50% - 8px);transform:translateY(16px)}.eui-tooltip__container .eui-tooltip.eui-tooltip--below{margin-top:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--below:before{border-color:transparent transparent var(--eui-base-color-grey-100) transparent;left:calc(50% - 8px);top:0;transform:translateY(-100%)}.eui-tooltip__container .eui-tooltip.eui-tooltip--left,.eui-tooltip__container .eui-tooltip.eui-tooltip--before{margin-right:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--left:before,.eui-tooltip__container .eui-tooltip.eui-tooltip--before:before{border-color:transparent transparent transparent var(--eui-base-color-grey-100);right:-8px;top:50%;transform:translate(8px,-8px)}.eui-tooltip__container .eui-tooltip.eui-tooltip--right,.eui-tooltip__container .eui-tooltip.eui-tooltip--after{margin-left:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--right:before,.eui-tooltip__container .eui-tooltip.eui-tooltip--after:before{border-color:transparent var(--eui-base-color-grey-100) transparent transparent;left:-8px;top:50%;transform:translate(-8px,-8px)}\n"], animations: [showHide], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-tooltip-container.component', animations: [showHide], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div [innerHTML]=\"config.tooltipContent\"></div>\n", styles: [".eui-tooltip__container{background-color:transparent}.eui-tooltip__container .eui-tooltip{background-color:var(--eui-base-color-grey-100);border-radius:var(--eui-base-border-radius);box-shadow:var(--eui-base-shadow-4);color:var(--eui-base-color-white);font-size:var(--eui-base-font-size-xs);max-width:50vw;opacity:.95;padding:var(--eui-base-spacing-xs);white-space:pre-line}.eui-tooltip__container .eui-tooltip:before{border-color:transparent;border-style:solid;border-width:8px;bottom:0;content:\"\";display:block;height:0;position:absolute;width:0}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-left{text-align:left}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-right{text-align:right}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-center{text-align:center}.eui-tooltip__container .eui-tooltip.eui-tooltip--content-justify{text-align:justify}.eui-tooltip__container .eui-tooltip.eui-tooltip--above{margin-bottom:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--above:before{border-color:var(--eui-base-color-grey-100) transparent transparent transparent;left:calc(50% - 8px);transform:translateY(16px)}.eui-tooltip__container .eui-tooltip.eui-tooltip--below{margin-top:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--below:before{border-color:transparent transparent var(--eui-base-color-grey-100) transparent;left:calc(50% - 8px);top:0;transform:translateY(-100%)}.eui-tooltip__container .eui-tooltip.eui-tooltip--left,.eui-tooltip__container .eui-tooltip.eui-tooltip--before{margin-right:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--left:before,.eui-tooltip__container .eui-tooltip.eui-tooltip--before:before{border-color:transparent transparent transparent var(--eui-base-color-grey-100);right:-8px;top:50%;transform:translate(8px,-8px)}.eui-tooltip__container .eui-tooltip.eui-tooltip--right,.eui-tooltip__container .eui-tooltip.eui-tooltip--after{margin-left:var(--eui-base-spacing-m)}.eui-tooltip__container .eui-tooltip.eui-tooltip--right:before,.eui-tooltip__container .eui-tooltip.eui-tooltip--after:before{border-color:transparent var(--eui-base-color-grey-100) transparent transparent;left:-8px;top:50%;transform:translate(-8px,-8px)}\n"] }]
        }], ctorParameters: function () { return [{ type: EuiTooltipConfig, decorators: [{
                    type: Inject,
                    args: [TOOLTIP_CONTAINER_CONFIG]
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { e2e: [{
                type: HostBinding,
                args: ['attr.data-e2e']
            }], cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], showHide: [{
                type: HostBinding,
                args: ['@showHide']
            }] } });

class EuiTooltipDirective {
    constructor(overlay, host, injector) {
        this.overlay = overlay;
        this.host = host;
        this.injector = injector;
        this.e2eAttr = 'eui-tooltip';
        this.showDelay = 0;
        this.hideDelay = 0;
        this.contentAlignment = 'center';
        this.position = 'above';
        this._isDisabled = false;
        this.originX = 'center';
        this.originY = 'top';
        this.overlayX = 'center';
        this.overlayY = 'bottom';
        this.fallbackOriginX = 'center';
        this.fallbackOriginY = 'bottom';
        this.fallbackOverlayX = 'center';
        this.fallbackOverlayY = 'top';
        this.destroy$ = new Subject();
        this.isOpen$ = new BehaviorSubject(false);
        this.mouseleaveSubscription = new Subscription();
        this.blurSubscription = new Subscription();
        this.showDelaySubscription = new Subscription();
    }
    get isDisabled() { return this._isDisabled; }
    set isDisabled(value) { this._isDisabled = coerceBooleanProperty(value); }
    get euiTooltip() {
        return this.tooltipContent;
    }
    set euiTooltip(value) {
        this.tooltipContent = value;
    }
    ngOnChanges() {
        this.setPosition();
    }
    ngOnInit() {
        this.setPosition();
        if (this.content) {
            this.contentAlignment = this.content;
        }
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.mouseleaveSubscription.unsubscribe();
        this.blurSubscription.unsubscribe();
        this.showDelaySubscription.unsubscribe();
        this.overlayRef?.dispose();
        this.overlayRef = null;
    }
    mouseEntering(event) {
        this.show(event.target);
    }
    focused(event) {
        this.show(event.target);
    }
    get isOpen() {
        return this.isOpen$.value;
    }
    show(origin = this.host.nativeElement) {
        this.showDelaySubscription = timer(this.showDelay).subscribe(() => {
            if (!this.isOpen && !this.isDisabled && this.tooltipContent) {
                const positionStrategy = this.overlay.position()
                    .flexibleConnectedTo(origin)
                    .withPositions([
                    new ConnectionPositionPair({ originX: this.originX, originY: this.originY }, { overlayX: this.overlayX, overlayY: this.overlayY }),
                    new ConnectionPositionPair({ originX: this.fallbackOriginX, originY: this.fallbackOriginY }, { overlayX: this.fallbackOverlayX, overlayY: this.fallbackOverlayY }),
                ])
                    .withFlexibleDimensions(false)
                    .withLockedPosition(true);
                const scrollStrategy = this.overlay.scrollStrategies.reposition({ scrollThrottle: 10 });
                this.overlayRef = this.overlay.create({
                    hasBackdrop: false,
                    panelClass: 'eui-tooltip__container',
                    positionStrategy,
                    scrollStrategy,
                });
                const componentPortal = new ComponentPortal(EuiTooltipContainerComponent, null, this.createInjector({
                    e2eAttr: this.e2eAttr,
                    tooltipContent: this.tooltipContent,
                    position: this.position,
                    contentAlign: this.contentAlignment,
                }));
                this.overlayRef.attach(componentPortal);
                this.isOpen$.next(true);
            }
        });
        this.mouseleaveSubscription = fromEvent(origin, 'mouseleave').subscribe(() => {
            this.hide();
        });
        this.blurSubscription = fromEvent(origin, 'blur').subscribe(() => {
            this.hide();
        });
    }
    hide() {
        timer(this.hideDelay).pipe(take(1)).subscribe(() => {
            this.isOpen$.next(false);
            this.mouseleaveSubscription.unsubscribe();
            this.blurSubscription.unsubscribe();
            this.showDelaySubscription.unsubscribe();
            this.overlayRef?.dispose();
            this.overlayRef = null;
        });
    }
    createInjector(data) {
        const injectorTokens = [{ provide: TOOLTIP_CONTAINER_CONFIG, useValue: data }];
        return Injector.create({
            parent: this.injector,
            providers: injectorTokens,
        });
    }
    setPosition() {
        if (this.position === 'above') {
            this.originY = 'top';
            this.overlayY = 'bottom';
            this.fallbackOriginY = 'bottom';
            this.fallbackOverlayY = 'top';
        }
        if (this.position === 'right' || this.position === 'after') {
            this.originX = 'end';
            this.originY = 'center';
            this.overlayX = 'start';
            this.overlayY = 'center';
            this.fallbackOriginX = 'start';
            this.fallbackOriginY = 'center';
            this.fallbackOverlayX = 'end';
            this.fallbackOverlayY = 'center';
        }
        if (this.position === 'below') {
            this.originY = 'bottom';
            this.overlayY = 'top';
            this.fallbackOriginY = 'top';
            this.fallbackOverlayY = 'bottom';
        }
        if (this.position === 'left' || this.position === 'before') {
            this.originX = 'start';
            this.originY = 'center';
            this.overlayX = 'end';
            this.overlayY = 'center';
            this.fallbackOriginX = 'end';
            this.fallbackOriginY = 'center';
            this.fallbackOverlayX = 'start';
            this.fallbackOverlayY = 'center';
        }
    }
}
/** @nocollapse */ EuiTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirective, deps: [{ token: i1.Overlay }, { token: i0.ElementRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiTooltipDirective, selector: "[euiTooltip]", inputs: { e2eAttr: "e2eAttr", showDelay: "showDelay", hideDelay: "hideDelay", content: "content", contentAlignment: "contentAlignment", position: "position", isDisabled: "isDisabled", euiTooltip: "euiTooltip" }, host: { listeners: { "mouseenter": "mouseEntering($event)", "focus": "focused($event)" } }, exportAs: ["euiTooltip"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiTooltip]',
                    exportAs: 'euiTooltip',
                }]
        }], ctorParameters: function () { return [{ type: i1.Overlay }, { type: i0.ElementRef }, { type: i0.Injector }]; }, propDecorators: { e2eAttr: [{
                type: Input
            }], showDelay: [{
                type: Input
            }], hideDelay: [{
                type: Input
            }], content: [{
                type: Input
            }], contentAlignment: [{
                type: Input
            }], position: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], euiTooltip: [{
                type: Input
            }], mouseEntering: [{
                type: HostListener,
                args: ['mouseenter', ['$event']]
            }], focused: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }] } });

class EuiTooltipDirectiveModule {
}
/** @nocollapse */ EuiTooltipDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiTooltipDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirectiveModule, declarations: [EuiTooltipContainerComponent,
        EuiTooltipDirective], imports: [CommonModule], exports: [EuiTooltipDirective] });
/** @nocollapse */ EuiTooltipDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiTooltipDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        EuiTooltipContainerComponent,
                        EuiTooltipDirective,
                    ],
                    exports: [
                        EuiTooltipDirective,
                    ],
                }]
        }] });

/* eslint-disable */
class EuiMaxLengthDirective {
    constructor(el, renderer, control) {
        this.el = el;
        this.renderer = renderer;
        this.control = control;
        this._isShowMaxlength = true;
        this.maxLengthReached = new EventEmitter();
    }
    get isShowMaxlength() { return this._isShowMaxlength; }
    set isShowMaxlength(value) { this._isShowMaxlength = coerceBooleanProperty(value); }
    ngOnInit() {
        // set default length calculation behavior
        this.setLengthCalcFactory((value) => value?.length || 0);
    }
    ngOnDestroy() {
        // remove all DOM added elements
        this.destroyMaxLengthBox();
        // unsubscribe from valueChanges
        if (this.valueChangesSubscription) {
            this.valueChangesSubscription.unsubscribe();
        }
    }
    ngOnChanges(changes) {
        if (changes['isShowMaxlength'] && this.maxLengthSpan) {
            if (this.isShowMaxlength) {
                this.createMaxLengthBox();
                this._refreshValue();
            }
            else {
                this.destroyMaxLengthBox();
            }
        }
        if (changes['length']) {
            if (this.maxLength < 0) {
                throw new Error('Length of an input or a textarea cannot be negative.');
            }
            this._refreshValue();
        }
    }
    ngAfterContentInit() {
        if (this.maxLength) {
            this.createMaxLengthBox();
            this._refreshValue();
        }
        // this subscription is needed apart from HostListener to Changes in case usage of this directive
        // in custom component that implements ngDefaultControl
        if (this.control?.valueChanges) {
            this.valueChangesSubscription = this.control.valueChanges.subscribe(() => {
                this._refreshValue();
            });
        }
    }
    onChange(event) {
        if (!this.control || this.control?.control.updateOn !== 'change') {
            this._refreshValue();
        }
    }
    onInput(event) {
        if (!this.control || this.control?.control.updateOn !== 'change') {
            this._refreshValue();
        }
    }
    onPaste(event) {
        // get the value from clipboard
        let value = (event.clipboardData || window['clipboardData']).getData('text');
        // calculate and set the maxLength based on the clipboard value
        let input = this.el.nativeElement;
        this.renderer.setProperty(input, 'maxLength', this.calculateMaxLength(value));
    }
    setLengthCalcFactory(fn) {
        this.lengthCalcFn = () => {
            const value = this.getValue();
            return fn(value);
        };
    }
    /**
     * calculates the maxLength of an input element. Calculation is made
     * against the value of the Model or Input Element.
     * @param value Optional the value to calculate max length against (override the Model one)
     * @private
     */
    calculateMaxLength(value) {
        const inputValue = this.getValue();
        return (value?.length || (inputValue?.length || 0) - this.lengthCalcFn()) + this.maxLength;
    }
    _refreshValue() {
        let input = this.el.nativeElement;
        let valueLength = this.lengthCalcFn();
        let remainingLength = this.maxLength - valueLength;
        this.renderer.setProperty(input, 'maxLength', this.calculateMaxLength());
        if (this.maxLengthSpan) {
            // based on the remaining length add/remove the error class
            if (remainingLength <= 0) {
                this.renderer.addClass(this.maxLengthSpan, 'eui-input-maxlength--invalid');
                this.maxLengthReached.emit(true);
            }
            else {
                this.renderer.removeClass(this.maxLengthSpan, 'eui-input-maxlength--invalid');
                this.maxLengthReached.emit(false);
            }
            // set the value of remaining characters
            if (remainingLength < 0) {
                // this feature is only available when the calculation of length is not custom
                // to support this feature you need to calculate this.maxLength also
                if (this.control) {
                    this.control.control.setValue(input.value.substring(0, this.calculateMaxLength()));
                }
                else {
                    input.value = input.value.substring(0, this.calculateMaxLength());
                }
                this.renderer.setProperty(this.maxLengthSpan, 'innerHTML', '0');
            }
            else if (remainingLength >= 0) {
                this.renderer.setProperty(this.maxLengthSpan, 'innerHTML', (this.maxLength - valueLength).toString());
            }
        }
    }
    /**
     * creates the maxLength box by adding it to DOM and attaching correct Classes
     * for Wrapper and Span (actual box holding max length)
     * @private
     */
    createMaxLengthBox() {
        // creates the span that holds the max length value which changes according to input
        this.maxLengthSpan = this.renderer.createElement('span');
        this.renderer.addClass(this.maxLengthSpan, 'eui-input-maxlength');
        this.renderer.setAttribute(this.maxLengthSpan, 'innerHTML', this.maxLength.toString());
        // create wrapper element
        let nativeElement = this.el.nativeElement;
        if (nativeElement != null) {
            // set width to 100%
            this.renderer.setStyle(nativeElement, 'width', '100%');
            let parent = nativeElement.parentNode;
            if (parent != null && this.isShowMaxlength) {
                // creates wrapper div of input and maxLength span
                this.wrapper = this.renderer.createElement('div');
                this.renderer.addClass(this.wrapper, 'eui-input-maxlength-wrapper');
                // swap, wrapper should wrap the nativeElement and maxLength span
                parent.replaceChild(this.wrapper, nativeElement);
                this.renderer.appendChild(this.wrapper, nativeElement);
                this.renderer.appendChild(this.wrapper, this.maxLengthSpan);
            }
        }
    }
    /**
     * destroys the maxLength box from DOM
     * @private
     */
    destroyMaxLengthBox() {
        // removes maxlength element from DOM
        if (this.maxLengthSpan) {
            this.renderer.removeChild(this.maxLengthSpan.parentElement, this.maxLengthSpan);
        }
        // remove wrapper from DOM
        if (this.wrapper) {
            // get parent node of wrapper
            let parent = this.wrapper.parentNode;
            let nativeElement = this.el.nativeElement;
            // swap wrapper and nativeElement
            parent?.replaceChild(nativeElement, this.wrapper);
            // removes wrapper element from DOM
            this.renderer.removeChild(this.wrapper.parentElement, this.wrapper);
        }
    }
    /**
     * returns the value based on whether control is present and control's
     * updateOn strategy.
     * @private
     */
    getValue() {
        let input = this.el.nativeElement;
        return this.control && this.control.control.updateOn === 'change' ? this.control.value : input.value;
    }
}
/** @nocollapse */ EuiMaxLengthDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$1.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiMaxLengthDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiMaxLengthDirective, selector: "\n    input[euiMaxlength],\n    textarea[euiMaxlength],\n    [ngModel][euiMaxlength],\n    [formControlName][euiMaxlength]", inputs: { maxLength: ["euiMaxlength", "maxLength"], isShowMaxlength: "isShowMaxlength" }, outputs: { maxLengthReached: "maxLengthReached" }, host: { listeners: { "change": "onChange($event)", "input": "onInput($event)", "paste": "onPaste($event)" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: `
    input[euiMaxlength],
    textarea[euiMaxlength],
    [ngModel][euiMaxlength],
    [formControlName][euiMaxlength]`,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$1.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }]; }, propDecorators: { maxLength: [{
                type: Input,
                args: ['euiMaxlength']
            }], isShowMaxlength: [{
                type: Input,
                args: ['isShowMaxlength']
            }], maxLengthReached: [{
                type: Output
            }], onChange: [{
                type: HostListener,
                args: ['change', ['$event']]
            }], onInput: [{
                type: HostListener,
                args: ['input', ['$event']]
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }] } });
class EuiMaxLengthDirectiveModule {
}
/** @nocollapse */ EuiMaxLengthDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiMaxLengthDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirectiveModule, declarations: [EuiMaxLengthDirective], imports: [CommonModule], exports: [EuiMaxLengthDirective] });
/** @nocollapse */ EuiMaxLengthDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMaxLengthDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiMaxLengthDirective],
                    declarations: [EuiMaxLengthDirective]
                }]
        }] });

/* eslint-disable */
/* eslint-enable */
class EuiHasPermissionDirective {
    constructor(element, templateRef, viewContainer, permissionService) {
        this.element = element;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.permissionService = permissionService;
        this.permissions = [];
        this.isHidden = true;
    }
    set hasPermission(val) {
        this.permissions = val;
        this.updateView();
    }
    ngOnInit() {
        this.updateView();
    }
    updateView() {
        if (this.checkPermission()) {
            if (this.isHidden) {
                this.viewContainer.createEmbeddedView(this.templateRef);
                this.isHidden = false;
            }
        }
        else {
            this.isHidden = true;
            this.viewContainer.clear();
        }
    }
    checkPermission() {
        let hasPermission = true;
        // for start it checks with and operator
        this.permissions.forEach((permission) => {
            hasPermission = this.permissionService.checkAttributePermission(permission) && hasPermission;
        });
        return hasPermission;
    }
}
/** @nocollapse */ EuiHasPermissionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirective, deps: [{ token: i0.ElementRef }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i1$2.EuiPermissionService }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiHasPermissionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiHasPermissionDirective, selector: "[euiHasPermission]", inputs: { hasPermission: "hasPermission" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiHasPermission]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i1$2.EuiPermissionService }]; }, propDecorators: { hasPermission: [{
                type: Input
            }] } });
class EuiHasPermissionDirectiveModule {
}
/** @nocollapse */ EuiHasPermissionDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiHasPermissionDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirectiveModule, declarations: [EuiHasPermissionDirective], imports: [CommonModule], exports: [EuiHasPermissionDirective] });
/** @nocollapse */ EuiHasPermissionDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiHasPermissionDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiHasPermissionDirective],
                    declarations: [EuiHasPermissionDirective],
                }]
        }] });

class EuiScrollHandlerDirective {
    constructor(document) {
        this.document = document;
        this.navIsFixed = false;
        this.appHeaderHeight = 0;
        this._uxScrollHandler = false;
    }
    get uxScrollHandler() { return this._uxScrollHandler; }
    set uxScrollHandler(value) { this._uxScrollHandler = coerceBooleanProperty(value); }
    ngOnInit() {
        this.appWrapper = this.document.getElementById('app-wrapper');
        this.appHeader = this.document.getElementById('app-header');
        if (this.appHeader) {
            this.appHeaderHeight = this.appHeader.scrollHeight;
        }
    }
    onWindowScroll() {
        if (this.uxScrollHandler && this.appHeaderHeight !== 0) {
            const number = this.document.body.scrollTop || this.document.documentElement.scrollTop; // Chrome || IE/FF
            if (number > this.appHeaderHeight) {
                this.navIsFixed = true;
                this.appWrapper.classList.add('shrink-header-active');
            }
            else if (this.navIsFixed && number < this.appHeaderHeight) {
                this.navIsFixed = false;
                this.appWrapper.classList.remove('shrink-header-active');
            }
        }
    }
}
/** @nocollapse */ EuiScrollHandlerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirective, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiScrollHandlerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiScrollHandlerDirective, selector: "[euiScrollHandler]", inputs: { uxScrollHandler: "uxScrollHandler" }, host: { listeners: { "window:scroll": "onWindowScroll()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiScrollHandler]'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { uxScrollHandler: [{
                type: Input
            }], onWindowScroll: [{
                type: HostListener,
                args: ['window:scroll', []]
            }] } });
class EuiScrollHandlerElementDirective {
    constructor(document) {
        this.document = document;
        this.navIsFixed = false;
    }
    onWindowScroll() {
        const element = this.document.getElementById(this.uxScrollHandlerElement);
        const number = this.document.body.scrollTop || this.document.documentElement.scrollTop; // Chrome || IE/FF
        if (number > this.toggleActiveHeight) {
            this.navIsFixed = true;
            element.classList.add(this.toggleClass);
        }
        else if (this.navIsFixed && number < this.toggleActiveHeight) {
            this.navIsFixed = false;
            element.classList.remove(this.toggleClass);
        }
    }
}
/** @nocollapse */ EuiScrollHandlerElementDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerElementDirective, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiScrollHandlerElementDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiScrollHandlerElementDirective, selector: "[euiScrollHandlerElement]", inputs: { uxScrollHandlerElement: "uxScrollHandlerElement", toggleClass: "toggleClass", toggleActiveHeight: "toggleActiveHeight" }, host: { listeners: { "window:scroll": "onWindowScroll()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiScrollHandlerElement]'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { uxScrollHandlerElement: [{
                type: Input
            }], toggleClass: [{
                type: Input
            }], toggleActiveHeight: [{
                type: Input
            }], onWindowScroll: [{
                type: HostListener,
                args: ['window:scroll', []]
            }] } });
class EuiScrollHandlerDirectiveModule {
}
/** @nocollapse */ EuiScrollHandlerDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiScrollHandlerDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirectiveModule, declarations: [EuiScrollHandlerDirective, EuiScrollHandlerElementDirective], imports: [CommonModule], exports: [EuiScrollHandlerDirective, EuiScrollHandlerElementDirective] });
/** @nocollapse */ EuiScrollHandlerDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiScrollHandlerDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiScrollHandlerDirective, EuiScrollHandlerElementDirective],
                    declarations: [EuiScrollHandlerDirective, EuiScrollHandlerElementDirective]
                }]
        }] });

/* https://www.npmjs.com/package/ng2-smooth-scroll
   https://github.com/kavil/ng2SmoothScroll/blob/master/lib/ng2-SmoothScroll.directive.ts
*/
class EuiSmoothScrollToDirective {
    constructor() { }
    onClick() {
        this.targetElement = document.getElementById(this.scrollTo);
        if (!this.targetElement) {
            return;
        }
        new SmoothScroll(this.targetElement, {
            duration: this.duration,
            offset: this.offset,
            easing: this.easing,
            callbackBefore: this.callbackBefore,
            callbackAfter: this.callbackAfter,
            containerId: this.containerId,
            middleAlign: this.middleAlign
        });
    }
    ;
}
/** @nocollapse */ EuiSmoothScrollToDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollToDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiSmoothScrollToDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiSmoothScrollToDirective, selector: "[euiScrollTo]", inputs: { scrollTo: ["uxScrollTo", "scrollTo"], duration: "duration", offset: "offset", easing: "easing", callbackBefore: "callbackBefore", callbackAfter: "callbackAfter", containerId: "containerId", middleAlign: "middleAlign" }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollToDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiScrollTo]'
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { scrollTo: [{
                type: Input,
                args: ['uxScrollTo']
            }], duration: [{
                type: Input,
                args: ['duration']
            }], offset: [{
                type: Input,
                args: ['offset']
            }], easing: [{
                type: Input,
                args: ['easing']
            }], callbackBefore: [{
                type: Input,
                args: ['callbackBefore']
            }], callbackAfter: [{
                type: Input,
                args: ['callbackAfter']
            }], containerId: [{
                type: Input,
                args: ['containerId']
            }], middleAlign: [{
                type: Input,
                args: ['middleAlign']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });
class EuiSmoothScrollDirective {
    constructor(el) {
        this.el = el;
    }
    onClick(target) {
        if (this.scrollOnClick) {
            this.scroll();
        }
    }
    ;
    ngOnInit() {
        this.scroll();
    }
    scroll() {
        if (typeof this.scrollIf === 'undefined' || this.scrollIf === true) {
            setTimeout(() => {
                new SmoothScroll(this.el.nativeElement, {
                    duration: this.duration,
                    offset: this.offset,
                    easing: this.easing,
                    callbackBefore: this.callbackBefore,
                    callbackAfter: this.callbackAfter,
                    containerId: this.containerId,
                    middleAlign: this.middleAlign
                });
            }, 0);
        }
    }
}
/** @nocollapse */ EuiSmoothScrollDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiSmoothScrollDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiSmoothScrollDirective, selector: "[euiSmoothScroll]", inputs: { scrollIf: "scrollIf", duration: "duration", offset: "offset", easing: "easing", callbackBefore: "callbackBefore", callbackAfter: "callbackAfter", containerId: "containerId", scrollOnClick: "scrollOnClick", middleAlign: "middleAlign" }, host: { listeners: { "click": "onClick($event.target)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[euiSmoothScroll]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { scrollIf: [{
                type: Input,
                args: ['scrollIf']
            }], duration: [{
                type: Input,
                args: ['duration']
            }], offset: [{
                type: Input,
                args: ['offset']
            }], easing: [{
                type: Input,
                args: ['easing']
            }], callbackBefore: [{
                type: Input,
                args: ['callbackBefore']
            }], callbackAfter: [{
                type: Input,
                args: ['callbackAfter']
            }], containerId: [{
                type: Input,
                args: ['containerId']
            }], scrollOnClick: [{
                type: Input,
                args: ['scrollOnClick']
            }], middleAlign: [{
                type: Input,
                args: ['middleAlign']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event.target']]
            }] } });
class SmoothScroll {
    constructor(element, options) {
        this.smoothScroll(element, options);
    }
    smoothScroll(element, options) {
        options = options || {};
        // Options
        let duration = options.duration || 800, offset = options.offset || 0, easing = options.easing || 'easeInOutQuart', callbackBefore = options.callbackBefore || function () { }, callbackAfter = options.callbackAfter || function () { }, container = document.getElementById(options.containerId) || null, containerPresent = (container != undefined && container != null), middleAlign = options.middleAlign || false;
        /**
         * Retrieve current location
         */
        let getScrollLocation = function () {
            if (containerPresent) {
                return container.scrollTop;
            }
            else {
                if (window.pageYOffset) {
                    return window.pageYOffset;
                }
                else {
                    return document.documentElement.scrollTop;
                }
            }
        };
        /**
         * Calculate easing pattern.
         *
         * 20150713 edit - zephinzer
         * - changed if-else to switch
         * @see http://archive.oreilly.com/pub/a/server-administration/excerpts/even-faster-websites/writing-efficient-javascript.html
         */
        let getEasingPattern = function (type, time) {
            switch (type) {
                case 'easeInQuad': return time * time; // accelerating from zero velocity
                case 'easeOutQuad': return time * (2 - time); // decelerating to zero velocity
                case 'easeInOutQuad': return time < 0.5 ? 2 * time * time : -1 + (4 - 2 * time) * time; // acceleration until halfway, then deceleration
                case 'easeInCubic': return time * time * time; // accelerating from zero velocity
                case 'easeOutCubic': return (--time) * time * time + 1; // decelerating to zero velocity
                case 'easeInOutCubic': return time < 0.5 ? 4 * time * time * time : (time - 1) * (2 * time - 2) * (2 * time - 2) + 1; // acceleration until halfway, then deceleration
                case 'easeInQuart': return time * time * time * time; // accelerating from zero velocity
                case 'easeOutQuart': return 1 - (--time) * time * time * time; // decelerating to zero velocity
                case 'easeInOutQuart': return time < 0.5 ? 8 * time * time * time * time : 1 - 8 * (--time) * time * time * time; // acceleration until halfway, then deceleration
                case 'easeInQuint': return time * time * time * time * time; // accelerating from zero velocity
                case 'easeOutQuint': return 1 + (--time) * time * time * time * time; // decelerating to zero velocity
                case 'easeInOutQuint': return time < 0.5 ? 16 * time * time * time * time * time : 1 + 16 * (--time) * time * time * time * time; // acceleration until halfway, then deceleration
                default: return time;
            }
        };
        /**
         * Calculate how far to scroll
         */
        let getEndLocation = function (element) {
            let location = 0, elementRect = element.getBoundingClientRect(), absoluteElementTop = elementRect.top + window.pageYOffset;
            if (middleAlign) {
                location = (absoluteElementTop + (element.offsetHeight / 2)) - (window.innerHeight / 2);
            }
            else {
                location = absoluteElementTop;
            }
            if (offset) {
                location = location - offset;
            }
            return Math.max(location, 0);
        };
        // Initialize the whole thing
        setTimeout(function () {
            let currentLocation = null, startLocation = getScrollLocation(), endLocation = getEndLocation(element), timeLapsed = 0, distance = endLocation - startLocation, percentage, position, scrollHeight, internalHeight;
            /**
             * Stop the scrolling animation when the anchor is reached (or at the top/bottom of the page)
             */
            let stopAnimation = function () {
                currentLocation = getScrollLocation();
                if (containerPresent) {
                    scrollHeight = container.scrollHeight;
                    internalHeight = container.clientHeight + currentLocation;
                }
                else {
                    scrollHeight = document.body.scrollHeight;
                    internalHeight = window.innerHeight + currentLocation;
                }
                if (( // condition 1
                position == endLocation) ||
                    ( // condition 2
                    currentLocation == endLocation) ||
                    ( // condition 3
                    internalHeight > scrollHeight)) { // stop
                    clearInterval(runAnimation);
                    callbackAfter(element);
                }
            };
            /**
             * Scroll the page by an increment, and check if it's time to stop
             */
            let animateScroll = function () {
                timeLapsed += 16;
                percentage = (timeLapsed / duration);
                percentage = (percentage > 1) ? 1 : percentage;
                position = startLocation + (distance * getEasingPattern(easing, percentage));
                if (containerPresent) {
                    container.scrollTop = position;
                }
                else {
                    window.scrollTo(0, position);
                }
                stopAnimation();
            };
            callbackBefore(element);
            let runAnimation = setInterval(animateScroll, 16);
        }, 0);
    }
}
class EuiSmoothScrollDirectiveModule {
}
/** @nocollapse */ EuiSmoothScrollDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiSmoothScrollDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirectiveModule, declarations: [EuiSmoothScrollToDirective, EuiSmoothScrollDirective], imports: [CommonModule], exports: [EuiSmoothScrollToDirective, EuiSmoothScrollDirective] });
/** @nocollapse */ EuiSmoothScrollDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSmoothScrollDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiSmoothScrollToDirective, EuiSmoothScrollDirective],
                    declarations: [EuiSmoothScrollToDirective, EuiSmoothScrollDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiHasPermissionDirective, EuiHasPermissionDirectiveModule, EuiMaxLengthDirective, EuiMaxLengthDirectiveModule, EuiScrollHandlerDirective, EuiScrollHandlerDirectiveModule, EuiScrollHandlerElementDirective, EuiSmoothScrollDirective, EuiSmoothScrollDirectiveModule, EuiSmoothScrollToDirective, EuiTemplateDirective, EuiTemplateDirectiveModule, EuiTooltipDirective, EuiTooltipDirectiveModule };
